import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { otherSide, teamKeyOf } from '@/components/stats/game/gameUtils';
import { statsGameApi } from '@/components/stats/game/statsGameApi';
import { useGameData, useCourtRosters } from '@/components/stats/game/hooks/useGameData';
import { useStatisticianPresence } from '@/components/stats/game/hooks/useStatisticianPresence';
import { useGameClock } from '@/components/stats/game/hooks/useGameClock';
import { useLineupFlow } from '@/components/stats/game/hooks/useLineupFlow';
import { Scoreboard } from '@/components/stats/game/Scoreboard';
import { ScoringColumn, ActionsColumn } from '@/components/stats/game/StatColumns';
import { PlayFeed } from '@/components/stats/game/PlayFeed';
import { ClockRow, BottomDock, VideoUrlBar } from '@/components/stats/game/GameBars';
import { GameSettingsMenu } from '@/components/stats/game/GameSettingsMenu';
import { LineupPicker } from '@/components/stats/game/LineupPicker';
import { PlayerPicker } from '@/components/stats/game/PlayerPicker';
import { SubPicker } from '@/components/stats/game/SubPicker';
import { BoxScoreModal } from '@/components/stats/game/boxscore/BoxScoreModal';
import { EditRosterModal } from '@/components/stats/game/EditRosterModal';
import { EditEntryModal } from '@/components/stats/game/EditEntryModal';
import { PeriodSetupModal, TimerExpiredModal, ClockEditModal } from '@/components/stats/game/GameDialogs';

const pendingStatLabel = (s) => (s.tone === 'made' ? `${s.label} Good` : s.tone === 'miss' ? `${s.label} Miss` : s.label);

export default function StatsGame() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const {
    game, boxscore, teamTotals, recentEntries, onCourt, loading, fetchAll,
    quarter, setQuarter, clock, setClock, videoUrl, setVideoUrl,
  } = useGameData(gameId);
  const { playersByTeam, onCourtPlayers, benchPlayers } = useCourtRosters(game, onCourt);
  useStatisticianPresence(game?.id);
  const clockCtl = useGameClock({ gameId, clock, setClock, quarter, setQuarter, fetchAll });

  const [activeTeam, setActiveTeam] = useState('a');
  const [pendingStat, setPendingStat] = useState(null);
  const [showSubPicker, setShowSubPicker] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [showBoxScore, setShowBoxScore] = useState(false);
  const [showEditRoster, setShowEditRoster] = useState(false);
  const [showClockEdit, setShowClockEdit] = useState(false);
  const [showPeriodSetup, setShowPeriodSetup] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  const lineup = useLineupFlow({
    gameId, game, loading, onCourt, recentEntries, fetchAll,
    onFreshStartersSet: () => setShowPeriodSetup(true),
  });

  const recordStat = async (player, statKey, team) => {
    try {
      await statsGameApi.record(gameId, { player_id: player.id, team, stat_type: statKey, quarter });
      fetchAll();
    } catch { toast.error('Failed to record stat'); }
  };

  const handlePickPlayer = (player, side) => {
    if (!pendingStat) return;
    recordStat(player, pendingStat.key, side === 'main' ? activeTeam : otherSide(activeTeam));
    setPendingStat(null);
  };

  const handleSwap = async (outIds, inIds) => {
    const outs = new Set(outIds);
    const newCourt = [...onCourt[activeTeam].filter(x => !outs.has(x)), ...inIds];
    try {
      const res = await statsGameApi.lineup(gameId, activeTeam, newCourt);
      if (!res.ok) throw new Error();
      toast.success(outIds.length > 1 ? `${outIds.length} substitutions made` : 'Substitution made');
      setShowSubPicker(false);
      fetchAll();
    } catch { toast.error('Substitution failed'); }
  };

  const undoLast = async () => {
    if (recentEntries.length === 0) { toast.info('Nothing to undo'); return; }
    try {
      await statsGameApi.undo(gameId, recentEntries[0].id);
      fetchAll();
      toast.info('Stat undone');
    } catch { /* ignore */ }
  };

  const changeQuarter = async (q) => {
    setQuarter(q);
    await statsGameApi.setQuarter(gameId, q);
    setShowMenu(false);
  };

  const endGame = async () => {
    if (!window.confirm('End this game? This will finalize the stats.')) return;
    await statsGameApi.end(gameId);
    navigate(`/stats/summary/${gameId}`);
  };

  const copyJoinCode = () => {
    if (!game?.join_code) return;
    navigator.clipboard?.writeText(game.join_code);
    toast.success(`Code copied: ${game.join_code}`);
  };

  const saveVideo = async () => {
    await statsGameApi.setVideo(gameId, videoUrl);
    toast.success('Video linked');
    setShowVideoInput(false);
  };

  const saveClock = async (totalSec, alsoSetLength) => {
    if (await clockCtl.saveClock(totalSec, alsoSetLength)) setShowClockEdit(false);
  };

  const confirmPeriodSetup = async (mins) => {
    if (!(await clockCtl.setPeriodLength(mins))) { toast.error('Could not save period length'); return; }
    setShowPeriodSetup(false);
    toast.success(`Period length set to ${mins} minute${mins === 1 ? '' : 's'}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0F2C] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!game) return <div className="p-8 text-center text-white">Game not found</div>;
  if (game.status === 'completed') {
    navigate(`/stats/summary/${gameId}`);
    return null;
  }

  const teamA = game.team_a;
  const teamB = game.team_b;
  const scoreA = teamTotals?.team_a?.pts || 0;
  const scoreB = teamTotals?.team_b?.pts || 0;
  const activeTeamObj = game[teamKeyOf(activeTeam)];
  const otherTeamObj = game[teamKeyOf(otherSide(activeTeam))];
  const quarterLengthMin = Math.max(1, Math.round((clock.quarter_length_sec || 600) / 60));

  return (
    <div className="min-h-screen bg-[#0A0F2C] text-white flex flex-col">
      <Scoreboard
        teamA={teamA} teamB={teamB} scoreA={scoreA} scoreB={scoreB} quarter={quarter}
        activeTeam={activeTeam} onSelectTeam={setActiveTeam}
        joinCode={game.join_code} onCopyJoinCode={copyJoinCode} onBack={() => navigate('/stats')}
      />
      {showVideoInput && <VideoUrlBar value={videoUrl} onChange={setVideoUrl} onSave={saveVideo} />}

      <div className="flex-1 grid grid-cols-12 gap-2 sm:gap-3 p-3">
        <ScoringColumn onStatTap={setPendingStat} />
        <PlayFeed
          recentEntries={recentEntries} boxscore={boxscore} playersByTeam={playersByTeam}
          quarter={quarter} onUndo={undoLast} onEdit={setEditingEntry}
        />
        <ActionsColumn
          onStatTap={setPendingStat}
          onSub={() => setShowSubPicker(true)}
          onFoul={() => setPendingStat({ key: 'foul', label: 'Foul', tone: 'danger' })}
        />
      </div>

      <ClockRow quarter={quarter} clock={clock} onOpenMenu={() => setShowMenu(true)}
        onToggle={clockCtl.toggleClock} onEdit={() => setShowClockEdit(true)} />
      <BottomDock activeTeamName={activeTeamObj.name} onMenu={() => setShowMenu(true)} onUndo={undoLast} />

      {showMenu && (
        <GameSettingsMenu
          quarter={quarter} quarterLengthSec={clock.quarter_length_sec} teamA={teamA} teamB={teamB}
          onClose={() => setShowMenu(false)}
          onChangeQuarter={changeQuarter}
          onQuickLength={clockCtl.setPeriodLength}
          onEditLineup={lineup.setPickingLineupFor}
          onEditRoster={() => setShowEditRoster(true)}
          onBoxScore={() => setShowBoxScore(true)}
          onVideo={() => setShowVideoInput(true)}
          onImport={() => navigate(`/stats/import/${gameId}`)}
          onEndGame={endGame}
        />
      )}

      {lineup.pickingLineupFor && (
        // key forces a fresh mount per team so selections don't carry over.
        <LineupPicker
          key={lineup.pickingLineupFor}
          team={game[teamKeyOf(lineup.pickingLineupFor)]}
          players={playersByTeam[lineup.pickingLineupFor]}
          onCancel={() => lineup.setPickingLineupFor(null)}
          onConfirm={lineup.confirmLineup}
        />
      )}

      {pendingStat && !lineup.pickingLineupFor && (
        <PlayerPicker
          statLabel={pendingStatLabel(pendingStat)}
          mainTeam={activeTeamObj}
          mainPlayers={onCourtPlayers[activeTeam]}
          otherTeam={otherTeamObj}
          otherPlayers={onCourtPlayers[otherSide(activeTeam)]}
          onCancel={() => setPendingStat(null)}
          onPick={handlePickPlayer}
        />
      )}

      {showSubPicker && (
        <SubPicker
          team={activeTeamObj}
          onCourt={onCourtPlayers[activeTeam]}
          bench={benchPlayers[activeTeam]}
          onCancel={() => setShowSubPicker(false)}
          onSwap={handleSwap}
        />
      )}

      {showBoxScore && (
        <BoxScoreModal
          game={game} boxscore={boxscore} teamTotals={teamTotals}
          quarter={quarter} scoreA={scoreA} scoreB={scoreB}
          onClose={() => setShowBoxScore(false)}
        />
      )}

      {showEditRoster && (
        <EditRosterModal game={game} onClose={() => setShowEditRoster(false)} onSaved={fetchAll} />
      )}

      {editingEntry && (
        <EditEntryModal entry={editingEntry} game={game} onClose={() => setEditingEntry(null)} onSaved={fetchAll} />
      )}

      {showPeriodSetup && (
        <PeriodSetupModal
          initialMinutes={quarterLengthMin}
          joinCode={game.join_code}
          onCopyJoinCode={copyJoinCode}
          onConfirm={confirmPeriodSetup}
        />
      )}

      {clockCtl.timerExpiredFor !== null && (
        <TimerExpiredModal
          endedQuarter={clockCtl.timerExpiredFor}
          quarterLengthMin={Math.round((clock.quarter_length_sec || 600) / 60)}
          scoreA={scoreA} scoreB={scoreB}
          onAdvance={clockCtl.advanceToNextPeriod}
          onDismiss={clockCtl.dismissTimer}
          onEndGame={async () => { clockCtl.dismissTimer(); await endGame(); }}
        />
      )}

      {showClockEdit && (
        <ClockEditModal clock={clock} onClose={() => setShowClockEdit(false)} onSave={saveClock} />
      )}
    </div>
  );
}
